import { useState, useEffect, useRef } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Sparkles, Send, Loader2, Lightbulb, User as UserIcon, Bot } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const SUGGESTIONS = [
  "¿Cuál es mi tasa de conversión? ¿Cómo puedo mejorarla?",
  "¿Cuánto tiempo demoro en promedio en cerrar una cotización?",
  "¿Cuáles son los motivos más comunes por los que rechazan mis cotizaciones?",
  "¿Qué clientes son los más valiosos según monto aceptado?",
  "¿Hay algún cuello de botella en mi pipeline?",
];

export default function InsightsPage() {
  const { plan } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [usage, setUsage] = useState({ used: 0, limit: 0, enabled: false });
  const scroller = useRef(null);

  const loadUsage = () => api.get("/insights/usage").then(r => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/insights/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
      setUsage(u => ({ ...u, used: data.used, limit: data.limit }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al consultar Insights");
    }
    setLoading(false);
  };

  const enabled = plan?.features?.insights_per_month > 0;

  if (!enabled) {
    return (
      <div className="max-w-2xl mx-auto pt-12 text-center" data-testid="insights-locked">
        <div className="bg-gradient-to-br from-blue-500 to-violet-600 w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4">
          <Sparkles size={28} className="text-white" />
        </div>
        <h1 className="text-3xl font-black text-slate-900">Cotflow Insights</h1>
        <p className="text-slate-600 mt-2 mb-6">
          Consulta a la IA sobre tus cotizaciones: tasa de conversión, motivos de rechazo,
          tiempos de cierre, clientes más valiosos y más.
        </p>
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-left mb-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Disponible en</div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li>• <strong>Pro</strong> — 30 consultas mensuales</li>
            <li>• <strong>Empresa</strong> — 200 consultas mensuales</li>
          </ul>
        </div>
        <Link to="/app/plan" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition" data-testid="upgrade-from-insights">
          Ver planes disponibles
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)]" data-testid="insights-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles size={22} className="text-blue-600" /> Cotflow Insights
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Consulta con IA sobre el desempeño de tus cotizaciones.</p>
        </div>
        <div className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-1.5" data-testid="insights-usage">
          <span className="text-slate-500">Uso este mes:</span> <strong>{usage.used}</strong> / {usage.limit}
        </div>
      </div>

      <div ref={scroller} className="flex-1 overflow-y-auto bg-white border border-slate-200 rounded-2xl p-4 space-y-4">
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <Lightbulb size={28} className="mx-auto text-blue-500 mb-3" />
            <p className="text-sm text-slate-600 mb-4">Prueba con una de estas preguntas:</p>
            <div className="flex flex-col gap-2 max-w-md mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)}
                  className="text-left text-sm px-4 py-2.5 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition"
                  data-testid={`suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${i}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-slate-200 text-slate-700" : "bg-gradient-to-br from-blue-500 to-violet-600 text-white"}`}>
              {m.role === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
            </div>
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center"><Bot size={14} /></div>
            <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-tl-sm text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Analizando tus datos…
            </div>
          </div>
        )}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} disabled={loading || usage.used >= usage.limit}
          placeholder={usage.used >= usage.limit ? "Alcanzaste el límite mensual" : "Escribe tu pregunta…"}
          className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          data-testid="insights-input" />
        <button type="submit" disabled={loading || !input.trim() || usage.used >= usage.limit}
          className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-40 flex items-center gap-1.5"
          data-testid="insights-send">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar
        </button>
      </form>
    </div>
  );
}
